import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TicketCardSkeleton } from '@/components/ui/skeleton';
import { NoTicketsState, NetworkErrorState } from '@/components/EmptyState';
import { CustomerBottomNav } from '@/components/BottomNav';
import { 
  Ticket, 
  Calendar, 
  Clock, 
  MapPin, 
  QrCode,
  LogOut,
  ArrowLeft,
  CheckCircle,
  Download,
  X,
  RefreshCw,
  ChevronRight
} from 'lucide-react';
import QRCode from 'react-qr-code';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { GAM_TICKET_LOGO } from '@/config/assets';

export default function MyTicketsPage() {
  const { isAuthenticated, isCustomer, user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const getAuthHeader = useCallback(() => {
    const userToken = localStorage.getItem('user_token');
    return { headers: { Authorization: `Bearer ${userToken}` } };
  }, []);

  const fetchTickets = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/api/user/tickets`, getAuthHeader());
      setTickets(response.data);
      
      if (isRefresh) toast.success('Tickets refreshed');
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      setError(err.message || 'Failed to load tickets');
      if (isRefresh) toast.error('Failed to refresh tickets');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    const userToken = localStorage.getItem('user_token');
    if (!userToken) {
      navigate('/');
      return;
    }
    fetchTickets();
  }, [navigate, fetchTickets]);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const downloadPDF = async (ticketId) => {
    try {
      setDownloading(ticketId);
      const response = await axios.get(
        `${API_URL}/api/tickets/${ticketId}/pdf`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket_${ticketId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Ticket downloaded!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download ticket');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case 'valid':
      case 'unused':
        return <Badge className="bg-green-100 text-green-700 border-0">Valid</Badge>;
      case 'used':
        return <Badge className="bg-gray-100 text-gray-600 border-0">Used</Badge>;
      case 'expired':
        return <Badge className="bg-red-100 text-red-700 border-0">Expired</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const isTicketUsed = (status) => {
    return status?.toLowerCase() === 'used';
  };

  const isTicketValid = (status) => {
    const normalizedStatus = status?.toLowerCase();
    return normalizedStatus === 'valid' || normalizedStatus === 'unused';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-nav">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-50 safe-top">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/events">
                <Button variant="ghost" size="icon" className="touch-target btn-press">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-lg font-bold">My Tickets</h1>
                <p className="text-xs text-muted-foreground">{user?.name || user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => fetchTickets(true)}
                disabled={refreshing}
                className="touch-target btn-press"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout}
                className="touch-target btn-press"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Stats Bar */}
        {!loading && !error && tickets.length > 0 && (
          <div className="flex gap-4 mb-6 animate-fade-in">
            <div className="flex-1 bg-white rounded-xl p-4 border border-border">
              <p className="text-2xl font-bold text-[#0055FF]">{tickets.length}</p>
              <p className="text-xs text-muted-foreground">Total Tickets</p>
            </div>
            <div className="flex-1 bg-white rounded-xl p-4 border border-border">
              <p className="text-2xl font-bold text-green-600">
                {tickets.filter(t => isTicketValid(t.status)).length}
              </p>
              <p className="text-xs text-muted-foreground">Valid</p>
            </div>
            <div className="flex-1 bg-white rounded-xl p-4 border border-border">
              <p className="text-2xl font-bold text-gray-500">
                {tickets.filter(t => isTicketUsed(t.status)).length}
              </p>
              <p className="text-xs text-muted-foreground">Used</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <TicketCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <NetworkErrorState onRetry={() => fetchTickets()} />
        )}

        {/* Empty State */}
        {!loading && !error && tickets.length === 0 && (
          <NoTicketsState onBrowse={() => navigate('/events')} />
        )}

        {/* Tickets List */}
        {!loading && !error && tickets.length > 0 && (
          <div className="space-y-4">
            {tickets.map((ticket, index) => (
              <Card 
                key={ticket.ticket_id}
                className="overflow-hidden bg-white border-border card-interactive animate-fade-in-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`ticket-${ticket.ticket_id}`}
              >
                <CardContent className="p-0">
                  <div 
                    className="p-4 cursor-pointer"
                    onClick={() => setSelectedTicket(selectedTicket?.ticket_id === ticket.ticket_id ? null : ticket)}
                  >
                    <div className="flex gap-4">
                      {/* Event Image */}
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-[#0055FF] flex-shrink-0 overflow-hidden">
                        {ticket.event_flyer ? (
                          <img 
                            src={ticket.event_flyer} 
                            alt={ticket.event_name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Ticket className="w-8 h-8 text-white/50" />
                          </div>
                        )}
                      </div>

                      {/* Ticket Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-sm sm:text-base line-clamp-2">
                            {ticket.event_name}
                          </h3>
                          {getStatusBadge(ticket.status)}
                        </div>
                        <div className="space-y-1 text-xs sm:text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{formatDate(ticket.event_date)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="line-clamp-1">{ticket.event_venue}</span>
                          </div>
                        </div>
                        {ticket.category && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            {ticket.category}
                          </Badge>
                        )}
                      </div>

                      {/* Expand Arrow */}
                      <ChevronRight 
                        className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${
                          selectedTicket?.ticket_id === ticket.ticket_id ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  </div>

                  {/* Expanded QR Code Section */}
                  {selectedTicket?.ticket_id === ticket.ticket_id && (
                    <div className="border-t border-border animate-fade-in">
                      {/* Blue bordered ticket card */}
                      <div className="p-4 bg-[#0055FF]">
                        <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                          {/* TICKET USED Banner for used tickets */}
                          {isTicketUsed(ticket.status) && (
                            <div className="bg-gray-500 text-white py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-mono font-bold tracking-[0.3em] text-sm">TICKET USED</span>
                              </div>
                            </div>
                          )}

                          {/* VALID Banner for valid tickets */}
                          {isTicketValid(ticket.status) && (
                            <div className="bg-green-500 text-white py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-mono font-bold tracking-[0.3em] text-sm">VALID TICKET</span>
                              </div>
                            </div>
                          )}

                          {/* Header */}
                          <div className="bg-gray-100 py-4 px-6 border-b-2 border-gray-800">
                            <p className="font-mono text-gray-400 text-xs tracking-[0.2em] text-center">================================</p>
                            <h3 className="font-mono text-xl font-bold text-gray-800 text-center my-2 tracking-wider">GAM TICKET</h3>
                            <p className="font-mono text-gray-400 text-xs tracking-[0.2em] text-center">================================</p>
                          </div>

                          {/* Event Details */}
                          <div className="p-6 border-b border-gray-200">
                            <div className="space-y-4">
                              <div>
                                <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">EVENT:</p>
                                <p className="font-mono text-lg font-semibold text-gray-900">{ticket.event_name}</p>
                              </div>
                              <div>
                                <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">DATE:</p>
                                <p className="font-mono text-lg text-gray-900">{ticket.event_date || 'TBA'}</p>
                              </div>
                              <div>
                                <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">TIME:</p>
                                <p className="font-mono text-lg text-gray-900">{ticket.event_time || 'TBA'}</p>
                              </div>
                              <div>
                                <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] mb-1">VENUE:</p>
                                <p className="font-mono text-lg text-gray-900">{ticket.event_venue || 'TBA'}</p>
                              </div>
                            </div>
                          </div>

                          {/* Ticket ID - Dashed border */}
                          <div className="py-4 px-6 border-b-2 border-dashed border-gray-300">
                            <p className="font-mono text-[11px] text-gray-400 tracking-[0.2em] text-center mb-1">TICKET ID:</p>
                            <p className="font-mono text-xl font-bold text-gray-900 text-center tracking-wider">{ticket.ticket_id}</p>
                          </div>

                          {/* QR Code - Large and clear - Uses simple ticket_id for easy scanning */}
                          <div className="p-6 bg-white flex justify-center">
                            <div className={`p-4 border-2 border-dashed border-gray-300 rounded-lg bg-white ${isTicketUsed(ticket.status) ? 'opacity-70' : ''}`}>
                              <QRCode 
                                value={ticket.ticket_id}
                                size={200}
                                level="H"
                                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                              />
                            </div>
                          </div>

                          {/* Footer message */}
                          <div className="py-3 px-6 bg-gray-50 border-t border-gray-200">
                            {isTicketValid(ticket.status) && (
                              <p className="text-xs text-green-600 text-center flex items-center justify-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                Show this QR code at the venue entrance
                              </p>
                            )}
                            {isTicketUsed(ticket.status) && (
                              <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                                <CheckCircle className="w-4 h-4" />
                                This ticket has already been used
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="p-4 bg-slate-50 flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPDF(ticket.ticket_id);
                          }}
                          disabled={downloading === ticket.ticket_id}
                          className="btn-press h-10 px-6"
                        >
                          {downloading === ticket.ticket_id ? (
                            <div className="spinner spinner-sm mr-2" />
                          ) : (
                            <Download className="w-4 h-4 mr-2" />
                          )}
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Browse Events CTA */}
        {!loading && !error && tickets.length > 0 && (
          <div className="mt-8 text-center animate-fade-in">
            <Link to="/events">
              <Button variant="outline" className="btn-press">
                Browse More Events
              </Button>
            </Link>
          </div>
        )}
      </main>

      {/* Bottom Navigation */}
      <div className="md:hidden">
        <CustomerBottomNav />
      </div>
    </div>
  );
}
